<template>
  <div class="picker-wrapper">
    <div v-if="canAddNew" class="add-new-field">
      <VTextField placeholder="Add a new Variable" type="text" v-model="newField" :maxlength="60" appearance="ghost" class="vivid-input" :class="{ 'picker-light-bg': lightBackground }" />
      <VButton connotation="cta" appearance="filled" label="Save" @click="addNewField" />
    </div>
    <template v-if="!loading">
      <VSearchableSelect
        :modelValue="selectedValue || ''"
        @update:modelValue="onFieldSelected"
        placeholder="Search and select bot variable"
        appearance="ghost"
        class="vivid-select"
        fixed-dropdown
        :loading="loading"
      >
        <VOption
          v-for="(field, index) in allFieldsList"
          :key="index"
          :value="field.name"
          :text="field.name"
          :label="field.name"
        />
        <template #no-matches>
          <span>No variables matching this filter!</span>
        </template>
      </VSearchableSelect>
    </template>
    <template v-else>
      <span>Loading variables...</span>
    </template>
  </div>
</template>

<script setup>
import apiInterceptor from '@/shared/apiInterceptor';
import { environment } from "/src/shared/env";
import { ref, onMounted, computed } from 'vue';
import { useUIStore } from '@/stores/ui';
import { validateCustomField } from '@/helpers/customFields.helpers';

const emit = defineEmits(['update:modelValue', 'change'])

const updateToast = (payload) => useUIStore().updateToast(payload);

const props = defineProps({
  modelValue: {
    type: String
  },
  closeOnClickOutside: {
    type: Boolean,
    default: true
  },
  openDropdownDefault: {
    type: Boolean,
    default: false
  },
  bot_id: {
    type: String,
    required: true
  },
  canAddNew: {
    type: Boolean,
    default: true
  },
  lightBackground: {
    type: Boolean,
    default: false
  }
})

const selectedValue = computed(() => {
  return props.modelValue ? props.modelValue.replace('bot_variable_', '') : '';
})

const loading = ref(false);
const allFields = ref([]);
const newField = ref();

onMounted(async () => {
  await fetchCustomFields();
})

const fetchCustomFields = async () => {
  // Skip when there's no bot context so we never hit bots//variables (405) [APICC-3444].
  if (!props.bot_id) return;
  try {
    loading.value = true;
    const data = await apiInterceptor.get(environment('VUE_APP_API_URL') + `bot-builder/v1/bots/${props.bot_id}/variables`);
    allFields.value = data;
  } catch (error) {
    updateToast({
      open: true,
      message: error.message,
      type: "danger"
    })
  } finally {
    loading.value = false;
  }
}

const allFieldsList = computed(() => allFields.value ?? []);

const addNewField = async () => {
  try {
    if (!props.bot_id) return;
    validateCustomField(newField.value, null, false, true);
    loading.value = true;
    const data = await apiInterceptor.post(environment('VUE_APP_API_URL') + `bot-builder/v1/bots/${props.bot_id}/variables`, { name: newField.value });
    updateToast({
      open: true,
      message: "Added bot variable successfully",
      type: "success"
    })
    newField.value = undefined;
    allFields.value.unshift(data);
    emit('update:modelValue', `bot_variable_${data.name}`);
    emit('change', data);
  } catch (error) {
    console.log("Exception", error);
    updateToast({
      open: true,
      message: error.message,
      type: "danger"
    })
  } finally {
    loading.value = false;
  }
}

const onFieldSelected = (value) => {
  if (!value) return;
  emit('update:modelValue', `bot_variable_${value}`);
  emit('change', { name: value });
}
</script>

<style scoped lang="scss">
.picker-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.add-new-field {
  display: flex;
  align-items: center;
  gap: 10px;
}

.vivid-input {
  flex: 1;
}

.vivid-select {
  width: 100%;
}
</style>

<style lang="scss">
/* Unscoped so it applies to Vivid web component host elements */
.picker-wrapper.picker-light-bg .vivid-input,
.picker-wrapper.picker-light-bg .vivid-select {
  background: #fff !important;
  border-radius: 5px;
}
</style>
